import { css } from 'src/ui/style/css'
import React, { ComponentType, Ref, useState } from 'react'
import { UserPanel } from 'src/layout/header/UserPanel'
import { View } from 'src/ui/basic/View'
import { useRouter } from '@core'
import { useTran } from 'src/i18n/useTran'
import { useTheme } from 'src/ui/style/useTheme'
import { icon } from 'src/ui/icon/icons'
import { Icon } from 'src/ui/icon/Icon'
import { onClickOutside } from 'src/ui/dom/onClickOutside'
import { LanguageSwitcher } from 'src/layout/header/LanguageSwitcher'
import { Divider } from 'src/ui/Divider'
import { pages } from 'src/routing/pages'
import { msg } from 'src/i18n/texts/keys'
import { headerDimension } from 'src/layout/header/Header'
import { verticalChildSpacing } from 'src/ui/style/mixins/layout'
import { UniversalLink } from 'src/routing/ui/UniversalLink'
import { useI18nDownloadTradingTermsLink } from 'src/business/static-pages/content/trading-terms/useI18nDownloadTradingTermsLink'
import { useChooseWarehouseLinkMobile } from 'src/business/landing-page/link/useChooseWarehouseLink'
import { Iconed } from 'src/ui/icon/Iconed'
import { useUser } from 'src/auth/user/useUser'
import { NavLink } from '../NavLink'
import { useSearchParams } from 'src/app/useSearchParams'

const setBodyScrollable = (scrollable: boolean) => {
    const bodyEl = document.querySelector('body')!
    const val = scrollable ? 'scroll' : 'hidden'
    bodyEl.style.overflowY = val
}

export const MobileNavigation: ComponentType = () => {
    const t = useTran()
    const th = useTheme()
    const user = useUser()
    const [mobileMenuIsOpen, setMobileMenuState] = useState(false)
    const limitedView = useSearchParams().get('limitedView') === 'true'
    if (limitedView) return null

    const closeMobileMenu = () => {
        setBodyScrollable(true)
        setMobileMenuState(false)
    }
    const openMobileMenu = () => {
        setBodyScrollable(false)
        setMobileMenuState(true)
    }

    const refClickableOutside: Ref<HTMLDivElement> = onClickOutside(() =>
        closeMobileMenu()
    )

    // noinspection DuplicatedCode for now same as in the footer (PagesColumn)
    const pageNavItems: [string, string][] = [
        [msg.ui.nav.items.home, pages.Landing.to()],
        [msg.ui.nav.items.boxes, useChooseWarehouseLinkMobile()],
        ...(process.env.SPA_SHOW_SHIPMENTS_NAV === 'true'
            ? [[msg.ui.nav.items.shipments, pages.Shipments.to()] as [string, string]]
            : []),
        [msg.ui.nav.items.help, pages.Help.to()],
        [msg.ui.nav.items.tradingTerms, useI18nDownloadTradingTermsLink()],
        [msg.ui.nav.items.storePolicy, pages.StorePolicy.to()],
        [msg.ui.nav.items.gdpr, pages.Gdpr.to()]
    ]

    return (
        <div
            ref={refClickableOutside}
            css={css`
                display: flex;
                align-items: center;
                gap: ${th.spacing.md};
            `}
        >
            {!mobileMenuIsOpen && <TopBarUserPanel />}
            <Icon
                icon={mobileMenuIsOpen ? icon.Close : icon.Menu}
                onClick={() =>
                    mobileMenuIsOpen ? closeMobileMenu() : openMobileMenu()
                }
                css={css`
                    color: ${th.colors.tertiary};
                    cursor: pointer;
                    font-size: 1.5em;
                `}
            />
            <View
                css={[
                    css`
                        justify-content: flex-start;
                        width: 100vw;
                        height: calc(100vh - ${headerDimension.mobileHeight});
                        height: calc(100dvh - ${headerDimension.mobileHeight});
                        display: ${mobileMenuIsOpen ? 'flex' : 'none'};
                        position: fixed;
                        top: ${headerDimension.mobileHeight};
                        left: 0;
                        right: 0;
                        background: ${th.colors.white};
                        z-index: ${headerDimension.zIndex - 1};
                        overflow-y: auto;
                    `
                ]}
            >
                <View
                    css={[
                        verticalChildSpacing(th.spacing.lg)(th),
                        css`
                            flex-direction: column;
                            align-items: flex-end;
                            justify-content: flex-start;
                            width: 100%;
                            background: ${th.colors.white};
                            padding: ${th.spacing.md} ${th.spacing.lg}
                                ${th.spacing.xl} ${th.spacing.lg};
                            box-shadow: ${th.boxShadow.bottomOnly};
                        `
                    ]}
                >
                    <View
                        horizontal
                        css={css`
                            width: 100%;
                            justify-content: ${user
                                ? 'space-between'
                                : 'flex-end'};
                            display: flex;
                        `}
                    >
                        <UserPanel onUserAction={closeMobileMenu} />
                    </View>
                    <View
                        css={css`
                            align-self: flex-end;
                            display: flex;
                            justify-content: flex-end;
                            position: relative;
                            z-index: 2;
                        `}
                    >
                        <LanguageSwitcher onValueChanged={closeMobileMenu} />
                    </View>
                    <View
                        css={css`
                            // Stretch because of divider
                            align-self: stretch;
                        `}
                    >
                        <Divider />
                    </View>
                    {pageNavItems.map(([label, to]) =>
                        to.includes('://') ? (
                            <UniversalLink
                                key={label}
                                to={to}
                                onClick={closeMobileMenu}
                                css={css`
                                    text-align: right;
                                `}
                            >
                                {t(label)}
                            </UniversalLink>
                        ) : (
                            <NavLink
                                key={label}
                                to={to}
                                onClick={closeMobileMenu}
                                uppercase={false}
                                textAlign="right"
                            >
                                {t(label)}
                            </NavLink>
                        )
                    )}
                </View>

                <div
                    css={[
                        css`
                            // Fills the remaining viewport below the header
                            width: 100%;
                            flex-grow: 1;
                            background: ${th.colors.white};
                            pointer-events: none;
                        `
                    ]}
                />
            </View>
        </div>
    )
}

const TopBarUserPanel: ComponentType = () => {
    const user = useUser()
    const r = useRouter()
    const t = useTran()

    return user ? (
        <NavLink
            css={css`
                // Turn off uppercase from parent
                text-transform: none;
            `}
            to={r.linkTo(pages.MyBoxes)}
        >
            <Iconed icon={icon.Person} color={false} iconRight>
                {user.firstName}
            </Iconed>
        </NavLink>
    ) : (
        <NavLink to={r.linkTo(pages.Login)}>
            {t(msg.ui.nav.items.login)}
        </NavLink>
    )
}
